import { Honeybadger } from '@honeybadger-io/react';
import { getCookie } from 'cookies-next';
import { pipe } from 'fp-ts/lib/function';
import { SSRConfig, appWithTranslation } from 'next-i18next';
import { DefaultSeo } from 'next-seo';
import type { AppProps } from 'next/app';
import getConfig from 'next/config';
import { useEffect, useState, useMemo, useCallback } from 'react';

import { ErrorAsync, Http, Observability } from '@catawiki/runtime-toolkit';
import {
  createI18n,
  I18nProvider,
  I18nInstance,
  ErrorBoundary,
  ErrorPage,
} from '@catawiki/cata-style';

import { LocalAssets } from '@/shared/api/assets';
import { fetchCsrf } from '@/shared/api/csrf';
import { registerAllComponents } from '@/shared/builderio';
import { Layout } from '@/shared/components/Layout';
import { HeaderSettings } from '@/shared/components/assets';
import { COOKIES } from '@/shared/constants/cookies';
import { getUserId } from '@/shared/cookies';
import { nullableData } from '@/shared/utils/http';
import { isInMobileApp } from '@/shared/utils/isInMobileApp';
import '../src/styles/application.scss';
import { APP_URL } from '@/shared/constants/appRoutes';

const { publicRuntimeConfig } = getConfig();

// https://docs.honeybadger.io/lib/javascript/reference/configuration.html
Honeybadger.configure({
  apiKey: publicRuntimeConfig.honeybadgerApiKey,
  environment: publicRuntimeConfig.stage,
  revision: publicRuntimeConfig.lastCommitSha,
  projectRoot: 'webpack://_N_E/./',
  developmentEnvironments: ['development', 'staging'],
});

Honeybadger.setContext({ user_id: getUserId() });

Honeybadger.beforeNotify((notice) => {
  // Suppress bidder app reporting because of translation insertion in DOM
  if (
    typeof window !== 'undefined' &&
    /error #(418|423|425)/.test(notice?.message ?? '')
  ) {
    return false;
  }
  return true;
});

registerAllComponents();

interface SharedProps extends SSRConfig, HeaderSettings {
  assetsData?: LocalAssets;
  locale?: I18nInstance['locale'];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  err?: any;
}

function MyApp({ Component, pageProps, router }: AppProps<SharedProps>) {
  const { locale, _nextI18Next, ...rest } = pageProps;

  const i18n = useMemo(() => createI18n(locale), [locale]);

  const getTranslations = useCallback(() => {
    const initialI18nStore: Record<
      string,
      { translations: Record<string, string> }
    > = _nextI18Next?.initialI18nStore || {};

    return Object.entries(initialI18nStore).reduce<
      I18nInstance['translations']
    >(
      (result, [storeLocale, { translations }]) => ({
        ...result,
        [storeLocale]: translations,
      }),
      {}
    );
  }, [_nextI18Next]);

  const [csrfToken, setCsrfToken] = useState<string>();
  useEffect(() => {
    if (!isInMobileApp()) return;
    fetchCsrf()
      .then(pipe(nullableData))
      .then((csrf) => {
        if (!csrf) return;
        setCsrfToken(csrf);
      })
      .catch(ErrorAsync.errorToLeft);
  }, []);

  useEffect(() => {
    Http.onFetchRequest((request) => {
      const token = getCookie(COOKIES.OAUTH_TOKEN);
      if (token) {
        request.headers.set('Authorization', `Bearer ${token}`);
      }

      if (csrfToken) {
        request.headers.set(COOKIES.S_CSRF_TOKEN, csrfToken);
      }
    });
  }, [csrfToken]);

  if (!pageProps?.assetsData || router.route === '/_error') {
    return <Component {...pageProps} err={pageProps.err} />;
  }

  return (
    <ErrorBoundary
      fallback={() => <ErrorPage withLogo appUrl={APP_URL} />}
      onError={(error) =>
        Observability.notifyHB(error, { fingerprint: 'app_error_boundary' })
      }
    >
      <Layout {...pageProps}>
        <DefaultSeo
          openGraph={{
            type: 'website',
            locale: pageProps.locale,
            url: `https://www.catawiki.com/${pageProps.locale}/`,
            site_name: 'Catawiki',
          }}
          twitter={{
            handle: '@catawiki',
            cardType: 'summary_large_image',
          }}
          facebook={{
            appId: '238226522900020',
          }}
          canonical={`https://www.catawiki.com/${pageProps.locale}/`}
        />
        <I18nProvider
          fallback={<Component locale={locale} {...rest} />}
          value={i18n}
          onLoad={getTranslations}
        >
          <Component {...pageProps} />
        </I18nProvider>
      </Layout>
    </ErrorBoundary>
  );
}

export default appWithTranslation(MyApp);
